/**
 * User management callables: the org's users, invites and access changes.
 * The invites list itself is a Firestore read (`listOrgInvites` in ./firestore).
 */
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'
import type { OrgUser, UserRole } from '../types'

/** Every user of the caller's org (admin only). */
export async function listOrgUsers(): Promise<OrgUser[]> {
  const fn = httpsCallable<Record<string, never>, { users: OrgUser[] }>(functions, 'listOrgUsers')
  const res = await fn({})
  return res.data.users
}

export interface CreateInviteInput {
  email: string
  role: UserRole
  clinicId: string | null
}

/** Creates an invite and returns its one-time token (stored hashed; never readable again). */
export async function createInvite(
  input: CreateInviteInput,
): Promise<{ token: string; expiresAt: string }> {
  const fn = httpsCallable<CreateInviteInput, { token: string; expiresAt: string }>(functions, 'createInvite')
  const res = await fn(input)
  return res.data
}

export async function revokeInvite(inviteId: string): Promise<void> {
  const fn = httpsCallable<{ inviteId: string }, { success: boolean }>(functions, 'revokeInvite')
  await fn({ inviteId })
}

export async function updateUserAccess(input: {
  targetUid: string
  role: UserRole
  clinicId: string | null
}): Promise<void> {
  const fn = httpsCallable<typeof input, { success: boolean }>(functions, 'updateUserAccess')
  await fn(input)
}

export async function deleteUser(targetUid: string): Promise<void> {
  const fn = httpsCallable<{ targetUid: string }, { success: boolean }>(functions, 'deleteUser')
  await fn({ targetUid })
}
